import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";
import Seo from "../components/Seo";
import { businessLd, breadcrumbLd, faqLd } from "../components/StructuredData";
import { services, servicesFaqs } from "../data/services";

export default function ServicesPage() {
  const jsonLd = [
    businessLd(),
    breadcrumbLd([
      { name: "Home", path: "/" },
      { name: "Services", path: "/services" },
    ]),
    faqLd(servicesFaqs),
  ];

  return (
    <>
      <Seo
        title="Interior Design & Custom Furniture Services in Nepal"
        description="Three stages, one craftsman: Consulting, Design, and Build. End-to-end interior design and bespoke furniture services for homes across Kathmandu Valley and Nepal."
        path="/services"
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-cream-50 pt-20 sm:pt-24">
        <div className="bg-cream-100 border-b border-cream-200 py-12 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-8">
            <p className="font-sans text-xs tracking-widest text-walnut-400 uppercase mb-3">
              How Ananda Works
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-matte-900 font-light leading-tight">
              Consulting. Design. Build.
            </h1>
            <p className="font-sans text-base text-matte-500 mt-4 max-w-2xl leading-relaxed">
              Three stages, one craftsman. From a 30-minute first call to the
              day your pieces are installed, Ananda owns every detail
              personally — no handoffs, no showroom middlemen.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-24">
          <div className="space-y-16 sm:space-y-24">
            {services.map((s, i) => (
              <div
                key={s.slug}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${
                  i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div>
                  <img
                    src={s.image}
                    alt={`${s.title} stage — ${s.headline}`}
                    className="w-full h-80 lg:h-[28rem] object-cover rounded-sm"
                    loading="lazy"
                    decoding="async"
                    width="1200"
                    height="700"
                  />
                </div>
                <div>
                  <p className="font-sans text-xs tracking-[0.3em] text-walnut-400 uppercase mb-3">
                    Stage {s.stage}
                  </p>
                  <h2 className="font-serif text-3xl sm:text-4xl text-matte-900 font-light leading-snug mb-4">
                    {s.title} — {s.headline}
                  </h2>
                  <p className="font-sans text-sm sm:text-base text-matte-500 leading-relaxed mb-6">
                    {s.description}
                  </p>
                  <ul className="space-y-2 mb-8">
                    {s.deliverables.map((d) => (
                      <li
                        key={d}
                        className="font-sans text-sm text-matte-700 flex gap-3 items-start"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-walnut-400 mt-2 flex-shrink-0" />
                        {d}
                      </li>
                    ))}
                  </ul>
                  <p className="font-sans text-xs text-matte-400 mb-6">
                    <span className="text-walnut-500 font-medium">Timing:</span>{" "}
                    {s.when}
                  </p>
                  <Link
                    to={`/services/${s.slug}`}
                    className="inline-flex items-center gap-2 font-sans text-sm text-walnut-600 hover:text-walnut-800 transition-colors group"
                  >
                    Learn more about {s.title.toLowerCase()}
                    <ArrowRight
                      size={14}
                      className="group-hover:translate-x-1 transition-transform"
                    />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        <section className="py-16 bg-matte-900">
          <div className="max-w-3xl mx-auto px-4 sm:px-8 text-center">
            <p className="font-sans text-xs tracking-[0.3em] text-walnut-400 uppercase mb-3">
              Common Questions
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl text-white font-light mb-10">
              Before You Book a Consultation
            </h2>
            <div className="text-left space-y-5">
              {servicesFaqs.map((f) => (
                <details
                  key={f.q}
                  className="group border border-matte-700 rounded-sm bg-matte-800/40"
                >
                  <summary className="cursor-pointer list-none p-5 font-serif text-base sm:text-lg text-cream-200 flex items-center justify-between">
                    {f.q}
                    <span className="text-walnut-400 group-open:rotate-45 transition-transform text-xl">
                      +
                    </span>
                  </summary>
                  <p className="font-sans text-sm text-matte-400 leading-relaxed px-5 pb-5">
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 sm:py-28 bg-cream-100">
          <div className="max-w-3xl mx-auto px-4 sm:px-8 text-center">
            <p className="font-sans text-xs tracking-[0.3em] text-walnut-400 uppercase mb-3">
              Ready when you are
            </p>
            <h2 className="font-serif text-3xl sm:text-5xl text-matte-900 font-light mb-5">
              Start with a free consultation
            </h2>
            <p className="font-sans text-sm sm:text-base text-matte-500 leading-relaxed mb-10">
              30 minutes, no obligation. Tell Ananda about your space and
              he'll tell you, plainly, what's worth doing.
            </p>
            <Link
              to="/book"
              className="inline-flex items-center gap-2 bg-matte-900 text-cream-100 font-sans text-sm tracking-wider px-10 py-4 hover:bg-walnut-700 transition-colors"
            >
              <Calendar size={16} />
              Book Your Free Consultation
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
